import { useState, useEffect } from 'react'
import {
  Globe,
  Bell,
  Shield,
  Mail,
  Smartphone,
  Save,
  Video,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Settings,
  Lock,
  MapPin,
  Facebook,
  Linkedin,
  Twitter,
  Info,
  DollarSign,
  Plus,
  Pencil,
  Trash2,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminApi, PayTierItem } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'
import { friendlyApiError } from '../../lib/utils/apiError'

interface PlatformSettings {
  platformName: string
  supportEmail: string
  timezone: string
  defaultSessionDuration: string
  autoRecording: boolean
  emailNotifications: boolean
  smsNotifications: boolean
  retentionDays: string
  location: string
  feedbackEmail: string
  facebookUrl: string
  tiktokUrl: string
  xUrl: string
  linkedinUrl: string
}

interface Integration {
  name: string
  configured: boolean
  description: string
}

const DEFAULTS: PlatformSettings = {
  platformName: 'Interview Dress Rehearsal',
  supportEmail: 'support@idr.com',
  timezone: 'UTC',
  defaultSessionDuration: '60',
  autoRecording: true,
  emailNotifications: true,
  smsNotifications: false,
  retentionDays: '30',
  location: '',
  feedbackEmail: '',
  facebookUrl: '',
  tiktokUrl: '',
  xUrl: '',
  linkedinUrl: '',
}

const TABS = ['General', 'Notifications', 'Security', 'Integrations', 'About', 'Pay Tiers'] as const
type Tab = typeof TABS[number]

const Toggle = ({
  name,
  checked,
  onChange,
  disabled,
  label,
  description,
  icon,
}: {
  name: string
  checked: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled: boolean
  label: string
  description: string
  icon: React.ReactNode
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      background: 'var(--accent-light)',
      borderRadius: 10,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ marginTop: 2, color: 'var(--primary-navy)' }}>{icon}</div>
      <div>
        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{label}</h4>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{description}</p>
      </div>
    </div>
    <label
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 48,
        height: 26,
        flexShrink: 0,
        pointerEvents: disabled ? 'none' : 'auto',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      />
      <span
        style={{
          position: 'absolute',
          cursor: 'pointer',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: checked ? 'var(--primary-navy)' : '#ccc',
          transition: '0.3s',
          borderRadius: 26,
        }}
      >
        <span
          style={{
            position: 'absolute',
            height: 20,
            width: 20,
            left: checked ? 25 : 3,
            bottom: 3,
            backgroundColor: 'white',
            transition: '0.3s',
            borderRadius: '50%',
          }}
        />
      </span>
    </label>
  </div>
)

export default function AdminSettings() {
  const { can } = usePermissionStore()
  const canEdit = can('settings', 'edit')

  const [activeTab, setActiveTab] = useState<Tab>('General')
  const [settings, setSettings] = useState<PlatformSettings>(DEFAULTS)
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Pay Tiers state
  const [payTiers, setPayTiers] = useState<PayTierItem[]>([])
  const [tiersLoading, setTiersLoading] = useState(false)
  const [editingTier, setEditingTier] = useState<PayTierItem | null>(null)
  const [editForm, setEditForm] = useState({ label: '', rate: '', description: '' })
  const [addForm, setAddForm] = useState({ name: '', label: '', rate: '', description: '' })
  const [showAddForm, setShowAddForm] = useState(false)
  const [tierSaving, setTierSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [settingsData, siteInfo, integrationsData] = await Promise.all([
          adminApi.getSettings().catch(() => ({})),
          adminApi.getSiteInfo().catch(() => ({})),
          adminApi.getIntegrationStatus().catch(() => []),
        ])
        setSettings((prev) => ({ ...prev, ...settingsData, ...siteInfo }))
        if (Array.isArray(integrationsData) && integrationsData.length) {
          setIntegrations(integrationsData)
        }
      } catch (err) {
        toast.error(friendlyApiError(err, 'Failed to load settings'))
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [])

  useEffect(() => {
    if (activeTab === 'Pay Tiers') {
      fetchPayTiers()
    }
  }, [activeTab])

  const fetchPayTiers = async () => {
    setTiersLoading(true)
    try {
      const data = await adminApi.getPayTiers()
      setPayTiers(data)
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to load pay tiers'))
    } finally {
      setTiersLoading(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target
    const value =
      target instanceof HTMLInputElement && target.type === 'checkbox'
        ? target.checked
        : target.value
    setSettings({ ...settings, [target.name]: value })
    setSaved(false)
  }

  const handleSave = async () => {
    if (!canEdit) return
    setSaving(true)
    try {
      const entries = Object.entries(settings).map(([key, value]) => ({
        key,
        value: String(value),
      }))
      await adminApi.updateSettings(entries)
      setSaved(true)
      toast.success('Settings saved successfully')
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to save settings'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setSettings(DEFAULTS)
    setSaved(false)
    toast.success('Settings reset to defaults')
  }

  const startEdit = (tier: PayTierItem) => {
    setEditingTier(tier)
    setEditForm({ label: tier.label, rate: String(tier.rate), description: tier.description ?? '' })
  }

  const cancelEdit = () => {
    setEditingTier(null)
    setEditForm({ label: '', rate: '', description: '' })
  }

  const handleUpdateTier = async () => {
    if (!editingTier) return
    const rate = parseFloat(editForm.rate)
    if (!editForm.label.trim() || isNaN(rate) || rate <= 0) {
      toast.error('Label and a valid rate are required.')
      return
    }
    setTierSaving(true)
    try {
      const updated = await adminApi.updatePayTier(editingTier.id, {
        label: editForm.label.trim(),
        rate,
        description: editForm.description.trim() || undefined,
      })
      setPayTiers((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
      cancelEdit()
      toast.success('Pay tier updated.')
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to update pay tier'))
    } finally {
      setTierSaving(false)
    }
  }

  const handleDeleteTier = async (id: number) => {
    if (!window.confirm('Deactivate this pay tier? It will no longer appear in the applications dropdown.')) return
    setDeletingId(id)
    try {
      await adminApi.deletePayTier(id)
      setPayTiers((prev) => prev.filter((t) => t.id !== id))
      toast.success('Pay tier deactivated.')
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to deactivate pay tier'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleAddTier = async () => {
    const rate = parseFloat(addForm.rate)
    if (!addForm.name.trim() || !addForm.label.trim() || isNaN(rate) || rate <= 0) {
      toast.error('Name, label, and a valid rate are required.')
      return
    }
    if (!/^[a-z0-9_]+$/.test(addForm.name.trim())) {
      toast.error('Name must be lowercase letters, numbers, and underscores only.')
      return
    }
    setTierSaving(true)
    try {
      const created = await adminApi.createPayTier({
        name: addForm.name.trim(),
        label: addForm.label.trim(),
        rate,
        description: addForm.description.trim() || undefined,
        sort_order: payTiers.length + 1,
      })
      setPayTiers((prev) => [...prev, created])
      setAddForm({ name: '', label: '', rate: '', description: '' })
      setShowAddForm(false)
      toast.success('Pay tier created.')
    } catch (err) {
      toast.error(friendlyApiError(err, 'Failed to create pay tier'))
    } finally {
      setTierSaving(false)
    }
  }

  const getIntegrationBadge = (name: string) => {
    const integration = integrations.find((i) => i.name === name)
    if (!integration) {
      if (loading) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: 'rgba(156,163,175,0.15)', color: '#6b7280' }}>
            <AlertCircle size={15} />
            <span>Checking…</span>
          </div>
        )
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#d97706' }}>
          <AlertCircle size={15} />
          <span>Not Connected</span>
        </div>
      )
    }
    if (integration.configured) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: 'rgba(16,185,129,0.1)', color: '#059669' }}>
          <CheckCircle size={15} />
          <span>Configured</span>
        </div>
      )
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#d97706' }}>
        <AlertCircle size={15} />
        <span>Not Connected</span>
      </div>
    )
  }

  const IntegrationRow = ({
    name,
    label,
    description,
    icon,
    iconBg,
    iconColor,
  }: {
    name: string
    label: string
    description: string
    icon: React.ReactNode
    iconBg: string
    iconColor: string
  }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'var(--accent-light)', borderRadius: 10, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: iconBg, color: iconColor, flexShrink: 0 }}>
          {icon}
        </div>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{label}</h4>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{description}</p>
        </div>
      </div>
      {getIntegrationBadge(name)}
    </div>
  )

  if (loading) {
    return <div className="loading-screen">Loading settings…</div>
  }

  const sectionHeader = (icon: React.ReactNode, title: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
      <div style={{ color: 'var(--primary-navy)' }}>{icon}</div>
      <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--primary-navy)', margin: 0 }}>{title}</h3>
    </div>
  )

  const saveBar = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 28 }}>
      {canEdit && (
        <button className="btn btn-secondary" onClick={handleReset}>
          Reset to Defaults
        </button>
      )}
      {canEdit ? (
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            'Saving…'
          ) : saved ? (
            <><CheckCircle size={17} style={{ marginRight: 6 }} />Saved</>
          ) : (
            <><Save size={17} style={{ marginRight: 6 }} />Save Changes</>
          )}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
          <Lock size={14} /> View Only
        </div>
      )}
    </div>
  )

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Platform Settings</h1>
        <p className="page-subtitle">Configure system-wide settings and preferences</p>
      </div>

      {!canEdit && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.35)', borderRadius: 10, marginBottom: 20, fontSize: 13, color: '#856404' }}>
          <Lock size={15} />
          <span><strong>View Only</strong> — You have read access to settings. Contact your Super Admin to request edit access.</span>
        </div>
      )}

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--border)', marginBottom: 28, overflowX: 'auto' }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 18px',
              fontSize: 14,
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? 'var(--primary-navy)' : 'var(--text-muted)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--primary-navy)' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'General' && (
        <>
          <div className="card">
            {sectionHeader(<Globe size={19} />, 'General Settings')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Platform Name</label>
                <input type="text" name="platformName" className="form-input" value={settings.platformName} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label">Support Email</label>
                <input type="email" name="supportEmail" className="form-input" value={settings.supportEmail} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label">Timezone</label>
                <select name="timezone" className="form-input" value={settings.timezone} onChange={handleChange} disabled={!canEdit}>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time — ET (UTC-5/4)</option>
                  <option value="America/Chicago">Central Time — CT (UTC-6/5)</option>
                  <option value="America/Denver">Mountain Time — MT (UTC-7/6)</option>
                  <option value="America/Los_Angeles">Pacific Time — PT (UTC-8/7)</option>
                  <option value="America/Sao_Paulo">Brazil — BRT (UTC-3)</option>
                  <option value="Europe/London">London — GMT/BST (UTC+0/1)</option>
                  <option value="Europe/Paris">Central Europe — CET (UTC+1/2)</option>
                  <option value="Europe/Moscow">Moscow — MSK (UTC+3)</option>
                  <option value="Asia/Dubai">Dubai — GST (UTC+4)</option>
                  <option value="Asia/Kolkata">India — IST (UTC+5:30)</option>
                  <option value="Asia/Dhaka">Bangladesh — BST (UTC+6)</option>
                  <option value="Asia/Bangkok">Bangkok — ICT (UTC+7)</option>
                  <option value="Asia/Singapore">Singapore — SGT (UTC+8)</option>
                  <option value="Asia/Tokyo">Japan — JST (UTC+9)</option>
                  <option value="Australia/Sydney">Sydney — AEDT (UTC+10/11)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Default Session Duration (minutes)</label>
                <select name="defaultSessionDuration" className="form-input" value={settings.defaultSessionDuration} onChange={handleChange} disabled={!canEdit}>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                </select>
              </div>
            </div>
          </div>
          {saveBar}
        </>
      )}

      {/* Notifications Tab */}
      {activeTab === 'Notifications' && (
        <>
          <div className="card">
            {sectionHeader(<Bell size={19} />, 'Notifications')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Toggle name="emailNotifications" checked={settings.emailNotifications} onChange={handleChange} disabled={!canEdit} label="Email Notifications" description="Send email alerts for sessions and updates" icon={<Mail size={17} />} />
              <Toggle name="smsNotifications" checked={settings.smsNotifications} onChange={handleChange} disabled={!canEdit} label="SMS Notifications" description="Send SMS reminders for upcoming sessions" icon={<Smartphone size={17} />} />
              <Toggle name="autoRecording" checked={settings.autoRecording} onChange={handleChange} disabled={!canEdit} label="Auto Recording" description="Automatically record all interview sessions" icon={<Settings size={17} />} />
            </div>
          </div>
          {saveBar}
        </>
      )}

      {/* Security Tab */}
      {activeTab === 'Security' && (
        <>
          <div className="card">
            {sectionHeader(<Shield size={19} />, 'Security Settings')}
            <div className="form-group">
              <label className="form-label">Recording Retention Period (days)</label>
              <select name="retentionDays" className="form-input" value={settings.retentionDays} onChange={handleChange} disabled={!canEdit}>
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            <p style={{ marginTop: 16, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Recordings older than the selected period are automatically deleted to comply with data retention policies.
            </p>
          </div>
          {saveBar}
        </>
      )}

      {/* Integrations Tab */}
      {activeTab === 'Integrations' && (
        <div className="card">
          {sectionHeader(<Globe size={19} />, 'Integrations')}
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, marginTop: -8 }}>
            Monitor connected services and third-party integrations.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <IntegrationRow name="zoom" label="Zoom" description="Video conferencing for interview sessions" icon={<Video size={19} />} iconBg="rgba(59,130,246,0.1)" iconColor="#2563eb" />
            <IntegrationRow name="stripe" label="Stripe" description="Payment processing for session fees" icon={<CreditCard size={19} />} iconBg="rgba(99,91,255,0.1)" iconColor="#635bff" />
          </div>
        </div>
      )}

      {/* About Tab */}
      {activeTab === 'About' && (
        <>
          <div className="card">
            {sectionHeader(<Info size={19} />, 'About Page')}
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, marginTop: -8 }}>
              Contact &amp; social links shown on the public About page.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MapPin size={13} /> Location
                </label>
                <input type="text" name="location" className="form-input" placeholder="e.g. San Francisco, CA" value={settings.location} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Mail size={13} /> Feedback Email
                </label>
                <input type="email" name="feedbackEmail" className="form-input" placeholder="e.g. feedback@example.com" value={settings.feedbackEmail} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Facebook size={13} /> Facebook URL
                </label>
                <input type="text" name="facebookUrl" className="form-input" placeholder="https://facebook.com/yourpage" value={settings.facebookUrl} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>TikTok</span> URL
                </label>
                <input type="text" name="tiktokUrl" className="form-input" placeholder="https://tiktok.com/@yourhandle" value={settings.tiktokUrl} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Twitter size={13} /> X (Twitter) URL
                </label>
                <input type="text" name="xUrl" className="form-input" placeholder="https://x.com/yourhandle" value={settings.xUrl} onChange={handleChange} disabled={!canEdit} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Linkedin size={13} /> LinkedIn URL
                </label>
                <input type="text" name="linkedinUrl" className="form-input" placeholder="https://linkedin.com/company/yourpage" value={settings.linkedinUrl} onChange={handleChange} disabled={!canEdit} />
              </div>
            </div>
          </div>
          {saveBar}
        </>
      )}

      {/* Pay Tiers Tab */}
      {activeTab === 'Pay Tiers' && (
        <div className="card">
          {sectionHeader(<DollarSign size={19} />, 'Pay Tiers')}
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, marginTop: -8 }}>
            Manage the pay tier options available when approving interviewer applications.
          </p>

          {tiersLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
          ) : (
            <>
              {/* Tiers table */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
                {/* Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 1fr auto', gap: 12, padding: '10px 16px', background: 'var(--accent-light)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <span>Label</span>
                  <span>Slug</span>
                  <span>Rate/hr</span>
                  <span>Description</span>
                  {canEdit && <span style={{ textAlign: 'right' }}>Actions</span>}
                </div>

                {payTiers.length === 0 && (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                    No active pay tiers. Add one below.
                  </div>
                )}

                {payTiers.map((tier, idx) => (
                  <div key={tier.id}>
                    {idx > 0 && <div style={{ borderTop: '1px solid var(--border)' }} />}
                    {editingTier?.id === tier.id ? (
                      /* Edit row */
                      <div style={{ padding: '14px 16px', background: 'rgba(212,175,55,0.05)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 1fr auto', gap: 12, alignItems: 'center' }}>
                          <input
                            className="form-input"
                            style={{ padding: '6px 10px', fontSize: 13 }}
                            value={editForm.label}
                            onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                            placeholder="Label"
                          />
                          <span style={{ fontSize: 13, color: 'var(--text-muted)', padding: '0 4px' }}>{tier.name}</span>
                          <input
                            className="form-input"
                            style={{ padding: '6px 10px', fontSize: 13 }}
                            type="number"
                            min="0"
                            step="0.01"
                            value={editForm.rate}
                            onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })}
                            placeholder="Rate"
                          />
                          <input
                            className="form-input"
                            style={{ padding: '6px 10px', fontSize: 13 }}
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Description"
                          />
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '6px 14px', fontSize: 13 }}
                              onClick={handleUpdateTier}
                              disabled={tierSaving}
                            >
                              {tierSaving ? '…' : 'Save'}
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '6px 12px', fontSize: 13 }}
                              onClick={cancelEdit}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Display row */
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 1fr auto', gap: 12, padding: '14px 16px', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{tier.label}</span>
                        <span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{tier.name}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary-navy)' }}>${tier.rate}/hr</span>
                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tier.description ?? '—'}</span>
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              title="Edit"
                              onClick={() => startEdit(tier)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-navy)', padding: 4 }}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              title="Deactivate"
                              onClick={() => handleDeleteTier(tier.id)}
                              disabled={deletingId === tier.id}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Tier */}
              {canEdit && (
                <>
                  {!showAddForm ? (
                    <button
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      onClick={() => setShowAddForm(true)}
                    >
                      <Plus size={16} /> Add Pay Tier
                    </button>
                  ) : (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 20, background: 'var(--accent-light)' }}>
                      <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, marginTop: 0 }}>New Pay Tier</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Slug <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(e.g. premium)</span></label>
                          <input
                            className="form-input"
                            placeholder="lowercase_only"
                            value={addForm.name}
                            onChange={(e) => setAddForm({ ...addForm, name: e.target.value.toLowerCase() })}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Label</label>
                          <input
                            className="form-input"
                            placeholder="e.g. Premium"
                            value={addForm.label}
                            onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Rate ($/hr)</label>
                          <input
                            className="form-input"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="e.g. 125"
                            value={addForm.rate}
                            onChange={(e) => setAddForm({ ...addForm, rate: e.target.value })}
                          />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Description <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                          <input
                            className="form-input"
                            placeholder="e.g. C-suite level interviews"
                            value={addForm.description}
                            onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button className="btn btn-primary" onClick={handleAddTier} disabled={tierSaving}>
                          {tierSaving ? 'Creating…' : 'Create Tier'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => { setShowAddForm(false); setAddForm({ name: '', label: '', rate: '', description: '' }) }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
